import Image from "next/image";
import React, { useRef, useState } from "react";
import { toWebp } from "@/utils/common";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import type { Swiper as SwiperType } from "swiper";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

const LABEL_COLORS = ["#0086E6", "#8ABC00"];

interface MobileSliderProps {
  cards?: any[];
}

const MobileSliderNew = ({ cards }: MobileSliderProps) => {
  const slides = cards || [];
  const [currentSlide, setCurrentSlide] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  const renderSlide = (slide: any, index: number) => {
    const imageUrl =
      slide?.mobile_image?.data?.attributes?.url ||
      slide?.image?.data?.attributes?.url;
    const labelColor = LABEL_COLORS[index % LABEL_COLORS.length];

    const eyebrow = slide?.title;
    const heading = slide?.description;
    const paragraph = slide?.description_2;

    return (
      <div className="w-full px-[0] text-center">
        {imageUrl && (
          <div className="relative mx-auto mb-[24px] h-[382px] w-auto">
            <Image
              src={toWebp(`${baseUrl}${imageUrl}`)}
              alt={eyebrow || "illustration"}
              fill
              sizes="382px"
              className="object-contain"
              unoptimized
            />
          </div>
        )}

        {eyebrow && (
          <p
            className="mb-[8px] text-[18px] font-semibold leading-[24px]"
            style={{ color: labelColor }}
          >
            {eyebrow}
          </p>
        )}
        {heading && (
          <h3 className="mb-[16px] text-[22px] font-bold leading-[31px] text-white">
            {heading}
          </h3>
        )}
        {paragraph && (
          <p className="text-[14px] leading-[22px] text-white">{paragraph}</p>
        )}
      </div>
    );
  };

  if (!slides.length) return null;

  const isLoopable = slides.length > 1;

  return (
    <div className="w-full">
      <Swiper
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setCurrentSlide(swiper.realIndex);
        }}
        modules={[Autoplay]}
        autoplay={
          isLoopable
            ? {
                delay: 3500,
                disableOnInteraction: false,
              }
            : false
        }
        loop={isLoopable}
        slidesPerView={1}
        className="w-full touch-pan-y select-none overflow-hidden cursor-grab active:cursor-grabbing"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.id ?? index}>
            {renderSlide(slide, index)}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Progress bar — one segment per card, active segment filled with the brand gradient */}
      {isLoopable && (
        <div className="mt-[32px] flex items-center justify-center gap-2 px-[24px]">
          {slides.map((_, index) => (
            <button
              key={index}
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => {
                if (swiperRef.current) {
                  swiperRef.current.slideToLoop(index);
                }
              }}
              className={`relative h-[4px] overflow-hidden rounded-full bg-[rgba(255,255,255,0.15)] transition-all duration-300 ${
                currentSlide === index ? "w-[140px]" : "w-[16px]"
              }`}
            >
              {currentSlide === index && (
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#8ABC00] to-[#0086E6]" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileSliderNew;
